import { propOr, compose, isEmpty, find, propEq, pathOr } from 'ramda';
import { ApolloError } from 'apollo-boost';
import { GraphQLError } from 'graphql';

enum BrandTypename {
  BrandAssetVenmoIcon = 'BrandAssetVenmoIcon',
  BrandAssetVenmoTile = 'BrandAssetVenmoTile',
  BrandAssetVenmoBackground = 'BrandAssetVenmoBackground',
  BrandAssetVenmoNote = 'BrandAssetVenmoNote',
  BrandAssetMerchantName = 'BrandAssetMerchantName',
  BrandAssetCard = 'BrandAssetCard',
  BrandAssetIcon = 'BrandAssetIcon',
  BrandAssetBanner = 'BrandAssetBanner',
  BrandAssetFeaturedBanner = 'BrandAssetFeaturedBanner',
  BrandAssetFeaturedBannerHeadline = 'BrandAssetFeaturedBannerHeadline',
  BrandAssetPrimaryColor = 'BrandAssetPrimaryColor',
  BrandAssetSecondaryColor = 'BrandAssetSecondaryColor',
  BrandAssetQRHeadline = 'BrandAssetQRHeadline',
  BrandAssetQRBanner = 'BrandAssetQRBanner',
}

enum BrandAssetComponent {
  background = 'background',
  icon = 'icon',
  tile = 'tile',
  venmoNote = 'venmoNote',
  venmoNoteQRC = 'venmoNoteQRC',
  merchantName = 'merchantName',
  cardImage = 'cardImage',
  banner = 'banner',
  featuredBanner = 'featuredBanner',
  featuredBannerHeadline = 'featuredBannerHeadline',
  primaryColor = 'primaryColor',
  secondaryColor = 'secondaryColor',
  QRHeadline = 'QRHeadline',
  QRBanner = 'QRBanner',
}

enum BrandBundleType {
  DEFAULTBRANDING = 'DEFAULT_BRANDING',
  VENMOAPP = 'VENMO_APP',
}

enum BrandMatcherType {
  REGEX = 'REGEX',
  MERCHANT_CATEGORY_CODE = 'MERCHANT_CATEGORY_CODE',
}

enum BrandTransactionMatcherTestCondition {
  SHOULD_MATCH = 'SHOULD_MATCH',
  SHOULD_NOT_MATCH = 'SHOULD_NOT_MATCH',
}

const DisplayLabels = {
  BrandMatcherTypesLabels: {
    REGEX: 'REGEX',
    MERCHANT_CATEGORY_CODE: 'Category Code',
  },
  BrandBundleTypes: {
    DEFAULT_BRANDING: 'Default Assets',
    VENMO_APP: 'Venmo Assets',
  },
};

type BrandTransactionMatcherTest = {
  value: string;
  condition: BrandTransactionMatcherTestCondition;
};

type Matcher = {
  type: string;
  pattern: string;
  options: string;
  tests?: BrandTransactionMatcherTest[];
};

type Values = {
  ticketId?: string | undefined;
  brandBundle?: string | undefined;
};

type BundleImage = {
  component: BrandAssetComponent;
  path: string;
  type: string;
  format?: string;
  width?: number;
  height?: number;
};

type BundleColor = {
  component: BrandAssetComponent;
  type: string;
  value: string;
};

type BundleComponent = {
  component: BrandAssetComponent;
  type: string;
  value: string;
};

type RawAssets = {
  updatedAt: string;
  bundle: BundleImage[];
};

type Matchers = {
  updatedAt?: string;
  matchers?: Matcher[];
};

type AssetsDefaultBranding = {
  updatedAt: string;
  card: BundleImage;
  icon: BundleImage;
  banner: BundleImage;
  featuredBanner: BundleImage;
  featuredBannerHeadline: BundleComponent;
  primaryColor: BundleColor;
  secondaryColor: BundleColor;
};

type AssetsVenmoApp = {
  updatedAt: string;
  tile: BundleImage;
  icon: BundleImage;
  background: BundleComponent;
  featuredBanner: BundleImage;
  featuredBannerHeadline: BundleComponent;
  note: BundleComponent;
  noteQRC: BundleComponent;
  merchantName: BundleComponent;
  QRHeadline: BundleComponent;
  QRBanner: BundleImage;
};

type BrandAsset = {
  id: string;
  name: string;
  description: string;
  assets: AssetsDefaultBranding | AssetsVenmoApp;
  rawAssets?: RawAssets;
  rawMatchers?: Matchers;
  rawPlaceMatchers?: Matchers;
};

type Merchant = {
  merchantId: string;
  merchantName: string;
  hasAccountDetails: boolean;
  brandId: string;
};

type AccountDetails = {
  merchantUuid?: string;
  companyName: string;
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone: string;
  category: string;
  subCategory?: string;
  pointOfSaleSystem?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postal: string;
  latitude?: number;
  longitude?: number;
  numberOfStores?: number;
};

type ApproveAssetResponse = {
  approveBrandAsset: {
    success: boolean;
  };
};

type AssetUpsertResponse = {
  upsertBrandAsset: {
    success: boolean;
  };
};

type ResolveZendeskTicketResponse = {
  updateTicket: {
    success: boolean;
  };
};

const getImageAsset = (component: string, bundle: BundleImage[]) =>
  compose(
    propOr('unknown', 'path'),
    find(propEq('component', component)),
  )(bundle);

const getImageFromPath = (path: string[], assets: BundleImage) =>
  pathOr('', path, assets);

const getCategoryText = (path: string[], category: any) =>
  pathOr('', path, category);

const getApolloError = (errors?: ApolloError | GraphQLError[]) =>
  pathOr('', ['graphQLErrors'], errors);

const defaultAssetApprovalSuccessMessage = [
  {
    displayMessage: 'Success!',
    message: 'Asset approval successful.',
  },
];

const defaultAssetUpsertSuccessMessage = [
  {
    displayMessage: 'Success!',
    message:
      'Asset update successful. Please have them approved to take effect.',
  },
];

const getImageDetails = (imageInfo: string) => {
  if (imageInfo === '') {
    return {
      width: 0,
      height: 0,
      format: '',
      path: '',
      publicId: '',
    };
  }

  const { secure_url, public_id, width, height, format } = JSON.parse(
    imageInfo,
  );

  return {
    width,
    height,
    format,
    path: secure_url,
    publicId: public_id,
  };
};

const buildBrandAssetImage = (
  imageInfo: string | undefined,
  type: string,
  component: BrandAssetComponent,
  typename: BrandTypename,
) => {
  if (imageInfo === '' || imageInfo === undefined) {
    return {};
  }

  const { secure_url, width, height, format } = JSON.parse(imageInfo);

  return {
    component,
    type,
    format,
    width,
    height,
    path: secure_url,
    __typename: typename,
  };
};

const buildBrandAssetColor = (
  value: string | undefined,
  type: string,
  component: BrandAssetComponent,
  typename: BrandTypename,
) => {
  if (value === '' || value === undefined) {
    return {};
  }

  return {
    component,
    type,
    value,
    version: '1.0.0',
    __typename: typename,
  };
};

const buildBundleComponent = (
  value: string | undefined,
  type: string,
  component: BrandAssetComponent,
  typename: BrandTypename,
) => {
  if (value === '' || value === undefined) {
    return {};
  }

  return {
    component,
    type,
    value,
    __typename: typename,
  };
};

const buildMatchers = (matchers: string[] = []) => {
  const newMatchers = matchers.map((matcher: string) => {
    if (!matcher || isEmpty(matcher)) return null;
    return {
      type: 'REGEX',
      pattern: matcher,
      options: 'i',
    };
  });
  return newMatchers.filter((matcher) => matcher != null);
};

const checkForPrevious = (previous: BundleImage, current: BundleImage) => {
  if ((!previous || isEmpty(previous)) && isEmpty(current)) {
    return {};
  }

  if (isEmpty(current)) {
    return previous;
  }

  return current;
};

export {
  Merchant,
  AccountDetails,
  BundleImage,
  BrandAsset,
  ApproveAssetResponse,
  AssetUpsertResponse,
  Values,
  ResolveZendeskTicketResponse,
  BrandBundleType,
  BrandMatcherType,
  Matcher,
  AssetsDefaultBranding,
  AssetsVenmoApp,
  BrandTypename,
  BrandAssetComponent,
  DisplayLabels,
  defaultAssetApprovalSuccessMessage,
  defaultAssetUpsertSuccessMessage,
  getImageAsset,
  getImageFromPath,
  getCategoryText,
  getApolloError,
  getImageDetails,
  buildBrandAssetImage,
  buildBrandAssetColor,
  buildMatchers,
  buildBundleComponent,
  checkForPrevious,
};
